import { Box, DialogContent, DialogTitle } from "@mui/material";
import { productBussiness } from "api/bussiness/product/product-bussiness";
import { UpdateDimensionAndWeightRequest } from "api/models/request/product/dimension-and-weight";
import { roundTo } from "commons/ultilities";
import { DynamicForm } from "commons/ultilities/dynamic-form/components/dynamic-form";
import { useForm } from "commons/ultilities/dynamic-form/hooks/use-form";
import { IControl } from "commons/ultilities/dynamic-form/models/control";
import { observer } from "mobx-react-lite";
import { DimensionAndWeightFixDecimalConstants } from "mobx-stores/_models/products/const";
import { useStores } from "mobx-stores/_root-store";
import { useCallback } from "react";
import { DialogForm } from "share-components/templates/form/dialog-form";
import { getAppearanceDescType } from "..";
import { getDimensionWeightFormSchema } from "./schema";

export const UpdateDimensionWeight = observer(() => {
  const {
    productManagement: {
      openUpdateDimensionAndWeight,
      setProp,
      productDetail,
      getProductDetail,
    },
  } = useStores();
  const {
    form: { controls, isValid },
    submitHandler,
    getControl,
  } = useForm<UpdateDimensionAndWeightRequest>(getDimensionWeightFormSchema(), {
    heightLower: productDetail?.dimensionWeight?.heightLower,
    heightStandard: productDetail?.dimensionWeight?.heightStandard,
    heightUpper: productDetail?.dimensionWeight?.heightUpper,
    lengthLower: productDetail?.dimensionWeight?.lengthLower,
    lengthStandard: productDetail?.dimensionWeight?.lengthStandard,
    lengthUpper: productDetail?.dimensionWeight?.lengthUpper,
    planeSpecLength: productDetail?.dimensionWeight?.planeSpecLength,
    planeSpecPaperSize: productDetail?.dimensionWeight?.planeSpecPaperSize,
    weightLower: productDetail?.dimensionWeight?.weightLower,
    weightStandard: productDetail?.dimensionWeight?.weightStandard,
    weightUpper: productDetail?.dimensionWeight?.weightUpper,
    widthLower: productDetail?.dimensionWeight?.widthLower,
    widthStandard: productDetail?.dimensionWeight?.widthStandard,
    widthUpper: productDetail?.dimensionWeight?.widthUpper,
    otherStandardSelect:
      productDetail?.dimensionWeight?.otherStandardSelect || "",
    otherStandardText: productDetail?.dimensionWeight?.otherStandardText || "",
    lengthUpperFactorNumber:
      productDetail?.dimensionWeight?.lengthUpperFactorNumber,
    lengthLowerFactorNumber:
      productDetail?.dimensionWeight?.lengthLowerFactorNumber,
    heightLowerFactorNumber:
      productDetail?.dimensionWeight?.heightLowerFactorNumber,
    heightUpperFactorNumber:
      productDetail?.dimensionWeight?.heightUpperFactorNumber,
    weightLowerFactorPercent:
      productDetail?.dimensionWeight?.weightLowerFactorPercent,
    weightUpperFactorPercent:
      productDetail?.dimensionWeight?.weightUpperFactorPercent,
    widthLowerFactorNumber:
      productDetail?.dimensionWeight?.widthLowerFactorNumber,
    widthUpperFactorNumber:
      productDetail?.dimensionWeight?.widthUpperFactorNumber,
    appearanceSelect: productDetail?.dimensionWeight?.appearanceSelect || "",
    appearanceText: productDetail?.dimensionWeight?.appearanceText || "",
    foldingStandardSize:
      productDetail?.dimensionWeight?.foldingStandardSize || "",
    foldingStandardCount:
      productDetail?.dimensionWeight?.foldingStandardCount || "",
  });
  const saveHandler = useCallback(
    async (data: UpdateDimensionAndWeightRequest) => {
      if (!productDetail || !productDetail?.id) return;
      await productBussiness.updateDimensionAndWeight(productDetail?.id, data);
      await getProductDetail(productDetail?.id);
      setProp("openUpdateDimensionAndWeight", false);
    },
    [getProductDetail, productDetail, setProp],
  );

  const calcUpperLength = useCallback(() => {
    const lengthStandard = getControl("lengthStandard").value as number | null;
    const lengthUpperFactorNumber = getControl("lengthUpperFactorNumber")
      .value as number | null;
    if (lengthStandard != null && lengthUpperFactorNumber != null) {
      const lengthUpper = lengthStandard + lengthUpperFactorNumber;

      getControl("lengthUpper").setValueAndValidate(
        roundTo(
          lengthUpper,
          DimensionAndWeightFixDecimalConstants.LENGTH_UPPER,
        ),
      );
    }
  }, [getControl]);

  const calcLowerLength = useCallback(() => {
    const lengthStandard = getControl("lengthStandard").value as number | null;
    const lengthLowerFactorNumber = getControl("lengthLowerFactorNumber")
      .value as number | null;
    if (lengthStandard != null && lengthLowerFactorNumber != null) {
      const lengthLower = lengthStandard - lengthLowerFactorNumber;

      getControl("lengthLower").setValueAndValidate(
        roundTo(
          lengthLower,
          DimensionAndWeightFixDecimalConstants.LENGTH_LOWER,
        ),
      );
    }
  }, [getControl]);

  const calcUpperWidth = useCallback(() => {
    const widthStandard = getControl("widthStandard").value as number | null;
    const widthUpperFactorNumber = getControl("widthUpperFactorNumber")
      .value as number | null;
    if (widthStandard != null && widthUpperFactorNumber != null) {
      const widthUpper = widthStandard + widthUpperFactorNumber;

      getControl("widthUpper").setValueAndValidate(
        roundTo(widthUpper, DimensionAndWeightFixDecimalConstants.WIDTH_UPPER),
      );
    }
  }, [getControl]);

  const calcLowerWidth = useCallback(() => {
    const widthStandard = getControl("widthStandard").value as number | null;
    const widthLowerFactorNumber = getControl("widthLowerFactorNumber")
      .value as number | null;
    if (widthStandard != null && widthLowerFactorNumber != null) {
      const widthLower = widthStandard - widthLowerFactorNumber;

      getControl("widthLower").setValueAndValidate(
        roundTo(widthLower, DimensionAndWeightFixDecimalConstants.WIDTH_LOWER),
      );
    }
  }, [getControl]);

  const calcUpperHeight = useCallback(() => {
    const heightStandard = getControl("heightStandard").value as number | null;
    const heightUpperFactorNumber = getControl("heightUpperFactorNumber")
      .value as number | null;
    if (heightStandard != null && heightUpperFactorNumber != null) {
      const heightUpper = heightStandard + heightUpperFactorNumber;

      getControl("heightUpper").setValueAndValidate(
        roundTo(
          heightUpper,
          DimensionAndWeightFixDecimalConstants.HEIGHT_UPPER,
        ),
      );
    }
  }, [getControl]);

  const calcLowerHeight = useCallback(() => {
    const heightStandard = getControl("heightStandard").value as number | null;
    const heightLowerFactorNumber = getControl("heightLowerFactorNumber")
      .value as number | null;
    if (heightStandard != null && heightLowerFactorNumber != null) {
      const heightLower = heightStandard - heightLowerFactorNumber;

      getControl("heightLower").setValueAndValidate(
        roundTo(
          heightLower,
          DimensionAndWeightFixDecimalConstants.HEIGHT_LOWER,
        ),
      );
    }
  }, [getControl]);

  const calcUpperWeight = useCallback(() => {
    const weightStandard = getControl("weightStandard").value as number | null;
    const weightUpperFactorPercent = getControl("weightUpperFactorPercent")
      .value as number | null;
    if (weightStandard != null && weightUpperFactorPercent != null) {
      const weightUpper =
        weightStandard + (weightStandard * weightUpperFactorPercent) / 100;

      getControl("weightUpper").setValueAndValidate(
        roundTo(
          weightUpper,
          DimensionAndWeightFixDecimalConstants.WEIGHT_UPPER,
        ),
      );
    }
  }, [getControl]);

  const calcLowerWeight = useCallback(() => {
    const weightStandard = getControl("weightStandard").value as number | null;
    const weightLowerFactorPercent = getControl("weightLowerFactorPercent")
      .value as number | null;
    if (weightStandard != null && weightLowerFactorPercent != null) {
      const weightLower =
        weightStandard - (weightStandard * weightLowerFactorPercent) / 100;

      getControl("weightLower").setValueAndValidate(
        roundTo(
          weightLower,
          DimensionAndWeightFixDecimalConstants.WEIGHT_LOWER,
        ),
      );
    }
  }, [getControl]);

  const handleValueChanged = useCallback(
    (control: IControl) => {
      //length
      if (control.name === "lengthStandard") {
        // calc upper length and lower length
        calcUpperLength();
        calcLowerLength();
      }

      if (control.name === "lengthUpperFactorNumber") {
        // calc upper length
        calcUpperLength();
      }

      if (control.name === "lengthLowerFactorNumber") {
        // calc lower length
        calcLowerLength();
      }
      //end length

      //width
      if (control.name === "widthStandard") {
        // calc upper width and lower width
        calcUpperWidth();
        calcLowerWidth();
      }

      if (control.name === "widthUpperFactorNumber") {
        // calc upper width
        calcUpperWidth();
      }

      if (control.name === "widthLowerFactorNumber") {
        // calc lower width
        calcLowerWidth();
      }

      //end width

      //height
      if (control.name === "heightStandard") {
        // calc upper height and lower height
        calcUpperHeight();
        calcLowerHeight();
      }

      if (control.name === "heightUpperFactorNumber") {
        // calc upper height
        calcUpperHeight();
      }

      if (control.name === "heightLowerFactorNumber") {
        // calc lower height
        calcLowerHeight();
      }
      //end height

      //weight
      if (control.name === "weightStandard") {
        // calc upper weight and lower weight
        calcUpperWeight();
        calcLowerWeight();
      }

      if (control.name === "weightUpperFactorPercent") {
        // calc upper weight
        calcUpperWeight();
      }

      if (control.name === "weightLowerFactorPercent") {
        // calc lower weight
        calcLowerWeight();
      }

      // appearance
      if (control.name === "appearanceSelect") {
        getControl("appearanceText").setValueAndValidate(
          getAppearanceDescType(control.value),
        );
      }

      //end appearance
    },
    [
      calcUpperLength,
      calcLowerLength,
      calcUpperWidth,
      calcLowerWidth,
      calcUpperHeight,
      calcLowerHeight,
      calcUpperWeight,
      calcLowerWeight,
      getControl,
    ],
  );

  return (
    <DialogForm
      open={openUpdateDimensionAndWeight}
      fullWidth
      maxWidth={"lg"}
      onClose={() => setProp("openUpdateDimensionAndWeight", false)}
      onCancelBtnClicked={() => setProp("openUpdateDimensionAndWeight", false)}
      onSaveBtnClick={async () => {
        await submitHandler(saveHandler);
      }}
      saveBtnProps={{
        disabled: !isValid,
      }}
    >
      <DialogTitle>
        {`Cập nhật thông số kỹ thuật: `}
        <span style={{ color: "blue" }}>{`${productDetail?.name}${
          productDetail?.customer?.dealingName
            ? `- ${productDetail?.customer?.dealingName}`
            : ""
        }`}</span>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ marginTop: "10px" }}>
          <DynamicForm
            controls={controls}
            onValueChanged={handleValueChanged}
          ></DynamicForm>
        </Box>
      </DialogContent>
    </DialogForm>
  );
});
